"use client";

import { Form, useDialogContext, useNotifications, useZodForm } from "@tosize/ui";
import type { ReactNode } from "react";
import { match } from "ts-pattern";
import { useRouter } from "~/hooks/useRouter";
import { createAccount } from "../../../../actions";
import { createAccountSchema } from "../../../../schemas";

interface Props {
	labels: {
		success: string;
		businessSuccess: string;
		email: string;
		passwordMatch: string;
	};
	lang: string;
	children: ReactNode;
	businessAccount?: boolean;
	redirect?: string;
	onSubmit?: () => void;
	actionAfterSuccess: "close" | "redirect" | null;
}

export function CreateAccountForm({ labels, onSubmit, ...props }: Props) {
	const form = useZodForm({
		schema: createAccountSchema.refine((data) => data.password === data.passwordConfirmation, {
			message: labels.passwordMatch,
			path: ["passwordConfirmation"],
		}),
	});
	const router = useRouter();
	const dialog = useDialogContext();
	const { notify } = useNotifications();

	return (
		<Form
			id="create-account-form"
			className="flex flex-col gap-4"
			form={form}
			onSubmit={async (data) => {
				dialog?.setForceOpen(true);

				const response = await createAccount({
					...data,
					lang: props.lang,
				});

				if (response?.serverError) {
					form.setError("email", { type: "value", message: labels.email });
				} else {
					notify({
						title: labels.success,
						subtitle: props.businessAccount ? labels.businessSuccess : undefined,
						variant: "success",
					});

					if (onSubmit) {
						return onSubmit();
					}

					match(props.actionAfterSuccess)
						.with("close", () => dialog?.onOpenChange?.(false))
						.with("redirect", () => {
							const redirectFallback = match(props.businessAccount)
								.with(true, () => "/customer/account/information/details/business")
								.with(false, () => "/customer/account")
								.otherwise(() => null);

							const url = props.redirect ?? redirectFallback;

							if (url) router.push(url);
						});
				}
			}}
		>
			{props.children}
		</Form>
	);
}
